/**
 * Base Reporting Component — thin wrapper around `useBaseReportingComponent`.
 */

import React, { ReactNode } from 'react';
import { Card } from 'antd';
import { useBaseReportingComponent } from './useBaseReportingComponent';
import { BaseReportingBody, BaseReportingControls } from './BaseReportingViews';
import type { ComponentData, ReportingComponentProps } from './BaseReportingComponent.types';
import styles from './BaseReportingComponent.module.css';

export type {
  DateRange,
  PeriodData,
  ComponentData,
  ReportingComponentProps,
} from './BaseReportingComponent.types';

export { useReportingComponentData } from './useReportingComponentData';

export const BaseReportingComponent: React.FC<
  ReportingComponentProps & {
    children?: (data: ComponentData, loading: boolean) => ReactNode;
  }
> = ({
  artifactId,
  componentType,
  initialDateRange,
  includeProjections = false,
  includeHistorical = true,
  isCardContext = false,
  className,
  onDataLoaded,
  onRefresh,
  header,
  footer,
  showControls = false,
  customDataFetcher,
  children,
}) => {
  const { state, handleDateRangeChange, handleProjectionToggle, handleRefresh } = useBaseReportingComponent({
    artifactId,
    componentType,
    initialDateRange,
    includeProjections,
    includeHistorical,
    onDataLoaded,
    onRefresh,
    customDataFetcher,
  });

  const containerClasses = [
    styles.baseReportingComponent,
    isCardContext ? styles.cardContext : styles.widgetContext,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Card className={containerClasses} variant={isCardContext ? 'borderless' : 'outlined'}>
      <div className={styles.headerRow}>
        {header && <div className={styles.customHeader}>{header}</div>}
      </div>

      <BaseReportingControls
        showControls={showControls}
        state={state}
        onDateRangeChange={handleDateRangeChange}
        onProjectionToggle={handleProjectionToggle}
        onRefresh={handleRefresh}
      />

      <div className={styles.content}>
        <BaseReportingBody state={state} children={children} />
      </div>

      {footer && <div className={styles.customFooter}>{footer}</div>}
    </Card>
  );
};

export default BaseReportingComponent;
